/**
 * ORDER SLICE
 * 
 * This slice handles all order-related state and API operations.
 * Uses middleware pattern for centralized API calls.
 * 
 * STATE STRUCTURE:
 * - orderData: Object containing order data
 * - createOrderLoading/Error/Success: States for POST /generate-order
 * 
 * API ENDPOINTS:
 * - POST /generate-order - Generate order (requires auth)
 * 
 * HOW TO USE IN COMPONENTS:
 * 
 * 1. Import what you need:
 *    import { generateOrder, selectOrderData, ... } from '../../redux/orderSlice';
 * 
 * 2. Dispatch actions:
 *    dispatch(generateOrder({ order_id: 1, event_id: 1 }));
 * 
 * 3. Read state with selectors:
 *    const orderData = useSelector(selectOrderData);
 *    const loading = useSelector(selectGenerateOrderLoading);
 *    const error = useSelector(selectGenerateOrderError);
 *    const success = useSelector(selectGenerateOrderSuccess);
 */

import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from './middleware/apiMiddleware';

// INITIAL STATE
const initialState = {
    orderData: null,
    generateOrderLoading: false,
    generateOrderError: null,
    generateOrderSuccess: false,
    bookTicketData: null,
    bookTicketLoading: false,
    bookTicketError: null,
    bookTicketSuccess: false,
    bookedSeatsData: null,
    selectedTables: null,
    updatePaymentIdData: null,
    updatePaymentIdLoading: false,
    updatePaymentIdError: null,
    updatePaymentIdSuccess: false,
};

// SLICE DEFINITION
const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        // ---------- GENERATE ORDER REDUCERS ----------
        beginGenerateOrderLoader: (state) => {
            state.generateOrderLoading = true;
            state.generateOrderError = null;
            state.generateOrderSuccess = false;
        },
        setGenerateOrderData: (state, action) => {
            state.generateOrderLoading = false;
            state.generateOrderError = null;
            state.generateOrderSuccess = true;
            state.orderData = action.payload;
        },
        generateOrderError: (state, action) => {
            state.generateOrderLoading = false;
            state.generateOrderError = action.payload?.errorMessage || 'Failed to generate order';
            state.generateOrderSuccess = false;
        },
        resetGenerateOrder: (state) => {
            state.generateOrderLoading = false;
            state.generateOrderError = null;
            state.generateOrderSuccess = false;
            state.orderData = null;
        },
        // ---------- BOOK TICKET REDUCERS ----------
        beginBookTicketLoader: (state) => {
            state.bookTicketLoading = true;
            state.bookTicketError = null;
            state.bookTicketSuccess = false;
        },
        setBookTicketData: (state, action) => {
            state.bookTicketLoading = false;
            state.bookTicketError = null;
            state.bookTicketSuccess = true;
            state.bookTicketData = action.payload;
        },
        bookTicketError: (state, action) => {
            state.bookTicketLoading = false;
            state.bookTicketError = action.payload?.errorMessage || 'Failed to book ticket';
            state.bookTicketSuccess = false;
        },
        resetBookTicket: (state) => {
            state.bookTicketLoading = false;
            state.bookTicketError = null;
            state.bookTicketSuccess = false;
            state.bookTicketData = null;
        },
        setBookedSeatsData: (state, action) => {
            state.bookedSeatsData = action.payload;
        },
        resetBookedSeatsData: (state) => {
            state.bookedSeatsData = null;
        },
        setSelectedTables: (state, action) => {
            state.selectedTables = action.payload;
        },
        resetSelectedTables: (state) => {
            state.selectedTables = null;
        },
        updateBookTicketStatus: (state, action) => {
            state.bookTicketData.transaction_id = action.payload;
        },
        // ---------- UPDATE PAYMENT ID REDUCERS ----------
        beginUpdatePaymentIdLoader: (state) => {
            state.updatePaymentIdLoading = true;
            state.updatePaymentIdError = null;
            state.updatePaymentIdSuccess = false;
        },
        setUpdatePaymentIdData: (state, action) => {
            state.updatePaymentIdLoading = false;
            state.updatePaymentIdError = null;
            state.updatePaymentIdSuccess = true;
            state.updatePaymentIdData = action.payload;
        },
        updatePaymentIdError: (state, action) => {
            state.updatePaymentIdLoading = false;
            state.updatePaymentIdError = action.payload?.errorMessage || 'Failed to update payment id';
            state.updatePaymentIdSuccess = false;
        },
        resetUpdatePaymentData: (state) => {
            state.updatePaymentIdLoading = false;
            state.updatePaymentIdError = null;
            state.updatePaymentIdSuccess = false;
            state.updatePaymentIdData = null;
        },
    },
});

// EXPORT ACTIONS
export const {
    beginGenerateOrderLoader,
    setGenerateOrderData,
    generateOrderError,
    resetGenerateOrder,
    beginBookTicketLoader,
    setBookTicketData,
    resetBookedSeatsData,
    setSelectedTables,
    resetSelectedTables,
    bookTicketError,
    resetBookTicket,
    setBookedSeatsData,
    updateBookTicketStatus,
    beginUpdatePaymentIdLoader,
    setUpdatePaymentIdData,
    updatePaymentIdError,
    resetUpdatePaymentData,
} = orderSlice.actions;

// THUNK ACTIONS
/**
 * GENERATE ORDER
 * 
 * Endpoint: POST /generate-order
 * Auth: Required (token in header)
 */
export const generateOrder = (orderData) => (dispatch) => {
    dispatch(apiCallBegan({
        url: '/generate-order',
        method: 'POST',
        data: orderData,
        onStart: beginGenerateOrderLoader.type,
        onSuccess: setGenerateOrderData.type,
        onError: generateOrderError.type,
        showGlobalError: false,
    }));
};

/**
 * BOOK TICKET
 * 
 * Endpoint: POST //update-ticket-booking
 * Auth: Required (token in header)
 */
export const bookTicket = (bookTicketData) => (dispatch) => {
    dispatch(apiCallBegan({
        url: '/update-ticket-booking',
        method: 'PUT',
        data: bookTicketData,
        onStart: beginBookTicketLoader.type,
        onSuccess: setBookTicketData.type,
        onError: bookTicketError.type,
        showGlobalError: false,
    }));
};

/**
 * UPDATE PAYMENT DETAILS
 * 
 * Endpoint: POST //update-ticket-booking
 * Auth: Required (token in header)
 */
export const updatePaymentId = (paymentDetailsData) => (dispatch) => {
    dispatch(apiCallBegan({
        url: '/update-payment-details',
        method: 'PUT',
        data: paymentDetailsData,
        onStart: beginUpdatePaymentIdLoader.type,
        onSuccess: setUpdatePaymentIdData.type,
        onError: updatePaymentIdError.type,
        showGlobalError: false,
    }));
};

// SELECTORS
export const selectOrderData = (state) => state.order.orderData;
export const selectGenerateOrderLoading = (state) => state.order.generateOrderLoading;
export const selectGenerateOrderError = (state) => state.order.generateOrderError;
export const selectGenerateOrderSuccess = (state) => state.order.generateOrderSuccess;
export const selectBookTicketData = (state) => state.order.bookTicketData;
export const selectBookTicketLoading = (state) => state.order.bookTicketLoading;
export const selectBookTicketError = (state) => state.order.bookTicketError;
export const selectBookTicketSuccess = (state) => state.order.bookTicketSuccess;
export const selectBookedSeatsData = (state) => state.order.bookedSeatsData;
export const selectSelectedTables = (state) => state.order.selectedTables;
export const selectUpdatePaymentIdData = (state) => state.order.updatePaymentIdData;
export const selectUpdatePaymentIdLoading = (state) => state.order.updatePaymentIdLoading;
export const selectUpdatePaymentIdError = (state) => state.order.updatePaymentIdError;
export const selectUpdatePaymentIdSuccess = (state) => state.order.updatePaymentIdSuccess;

// EXPORT SLICE
export default orderSlice.reducer;